import { CheckCircle, Warning } from '@mui/icons-material';
import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import {
  gql, useMutation, useQuery,
} from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Fragment, useContext, useState } from 'react';
import { Box, Typography } from '../../../../1-primative';
import {
  CircularProgress,
  StatusTag, Table, TableBody, TableCell, TableHeadCell, TableRow,
} from '../../../../2-component';
import { translateBackend } from '../../../../../assets/i18n/config';
import { ViewFormDialog } from './viewFormDialog';
import { WorkflowCompletionForm } from '../../components/workflowCompletionForm';
import { FileDocumentObjectTypes, FileDocumentTypes } from '../../../../../interfaces/fileDocument';
import { useFileUpload } from '../../../../../providers/fileUploadHook';
import { UserContext } from '../../../../../providers/userContextProvider';

const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        translatedName {
          en
          fr
        }
        state
        signingType
        requiredSignatures {
          signerKey
        }
        integrationFormPack {
          id
          state
        }
      }
      totalCount
    }
  }
`;

const SIGN_FORM_COMPLETION = gql`
  mutation signFormCompletion($input: SignFormCompletionInput!) {
    signFormCompletion(input: $input) {
      formCompletion {
        id
        state
      }
    }
  }
`;

export const SignFormCompletions = ({
  options, onNext, workflowCompletion,
}: {
  options: any,
  onNext: () => void;
  workflowCompletion: any;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'client']);
  const [activeFormCompletion, setActiveFormCompletion] = useState<any>(null);
  const [activeIndex, setIndex] = useState<any>(0);
  const [file, setFile] = useState<File>();
  const [signLoading, setSignLoading] = useState<boolean>(false);
  const { uploadFile } = useFileUpload();
  const { activeOrganization } = useContext(UserContext);

  // Fetch form completions for this workflow
  const { data, loading, refetch } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100, // Fetch all forms for this workflow
          sortField: 'translatedName.en',
          sortDesc: false,
        },
      },
    },
    skip: !workflowCompletion?.id,
    pollInterval: 30000,
    fetchPolicy: 'no-cache',
  });

  const formCompletions = data?.fetchFormCompletions?.formCompletions || [];

  // Separate regular forms from integration forms
  const forms = formCompletions.filter((f: any) => f.signingType !== 'INTEGRATION') ?? [];

  // Group integration forms by pack
  const integrationForms = formCompletions.filter((f: any) => f.signingType === 'INTEGRATION') ?? [];
  const groupedPacks = integrationForms.reduce((acc: any, form: any) => {
    const packId = form.integrationFormPack?.id;
    if (!packId) return acc;

    if (!acc[packId]) {
      acc[packId] = {
        id: packId,
        state: form.integrationFormPack?.state || 'SENT',
        forms: [],
      };
    }
    acc[packId].forms.push(form);
    return acc;
  }, {});
  const packs = Object.values(groupedPacks);

  const [signFormCompletion] = useMutation(SIGN_FORM_COMPLETION, {
    onCompleted: () => {
      // Refetch form completions to get updated state
      refetch();

      if (activeIndex + 1 < forms.length) {
        setIndex(activeIndex + 1);
        setActiveFormCompletion(forms[activeIndex + 1]);
      } else {
        setActiveFormCompletion(null);
      }
    },
  });

  const handleSign = async () => {
    setSignLoading(true);
    if (file) {
      await uploadFile(file, {
        objectType: FileDocumentObjectTypes.FORM_COMPLETION_SIGNED,
        objectId: activeFormCompletion?.id,
        sourceId: activeFormCompletion?.id,
        sourceType: 'FORM_COMPLETION',
        fileName: file.name,
        mediaType: file.type,
        type: FileDocumentTypes.TEMPLATE,
        permissionType: 'PUBLIC',
        name: file.name,
        organizationId: activeOrganization.id,
      });
      setFile(undefined);
    }
    await signFormCompletion({
      variables: {
        input: {
          formCompletionId: activeFormCompletion?.id,
        },
      },
    });
    setSignLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{
        height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
        <CircularProgress/>
      </Box>
    );
  }

  return (
    <WorkflowCompletionForm onSubmit={() => onNext()} submitButtonProps={{
      label: t('continue'),
      disabled: (!options.allowContinuingWithoutSigning && (forms.map((x: any) => x.state).includes('UNSIGNED') || packs?.some((pack: any) => pack.state !== 'COMPLETED'))),
      dataTestId: 'sign-continue-button',
    }}>
      <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
        {options?.title && (
          <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options?.title)}
        </Typography>
        )}
      {options?.subtitle && (
        <Typography display='inline-flex' alignItems='end' variant='bodyLarge' component='div' sx={{ mb: 3, table: { width: '100%' } }}>
          <Box display='inline-block'>
            <ReactMarkdown linkTarget="_blank" remarkPlugins={[remarkGfm]}>{translateBackend(options?.subtitle)}</ReactMarkdown>
          </Box>
        </Typography>
      )}
      <Table sx={{ mb: 3 }}>
        <TableBody data-testid='documents-table'>
          <TableRow>
            <TableHeadCell sx={{ width: '100%' }}>{t('client:document')}</TableHeadCell>
            <TableHeadCell sx={{ minWidth: '140px' }}>{t('client:status')}</TableHeadCell>
          </TableRow>
          { forms.map((formCompletion: any, index: number) => (
            <TableRow
              data-testid={`document-${index}`}
              key={index} hover pointer
              onClick={() => {
                setActiveFormCompletion(formCompletion);
                setIndex(index);
              }}
            >
              <TableCell>{translateBackend(formCompletion.translatedName)}</TableCell>
              <TableCell>
                <StatusTag
                  color={formCompletion.state === 'SIGNED' ? 'positive' : 'warning'}
                  icon={formCompletion.state === 'SIGNED' ? CheckCircle : Warning}
                  label={formCompletion.state === 'SIGNED' ? t('client:signed') : t('client:notSigned')}
                />
              </TableCell>
            </TableRow>
          ))}
          {packs?.map((pack: any, packIndex: number) => (
            <Fragment key={`pack-${packIndex}`}>
              <TableRow hover>
                <TableCell>
                  <Typography variant='bodyLarge'>{t('client:formPackage')}</Typography>
                  <ul>
                    {pack.forms?.map((formCompletion: any, formIndex: number) => (
                      <li key={`pack-${packIndex}-form-${formIndex}`}>{translateBackend(formCompletion.translatedName)}</li>
                    ))}
                  </ul>
                </TableCell>
                <TableCell>
                  <StatusTag
                    color={pack.state === 'COMPLETED' ? 'positive' : 'warning'}
                    icon={pack.state === 'COMPLETED' ? CheckCircle : Warning}
                    label={t(`client:formPackStates.${pack.state}`)}
                  />
                </TableCell>
              </TableRow>
            </Fragment>
          ))}
        </TableBody>
      </Table>
      { activeFormCompletion && (
        <ViewFormDialog formCompletionId={activeFormCompletion.id} onClose={() => setActiveFormCompletion(null)} sign={handleSign} file={file} setFile={setFile} signLoading={signLoading} />
      )}
    </Box>
    </WorkflowCompletionForm>
  );
};
